/*
 * @description: description
 * @Author: WangWei18
 */
import { useCallback, useEffect, useState, useRef } from 'react';
import { APIPath, HookInfo, SwaggerFetch, ReturnRequireData } from './types';

const INIT_INFO: HookInfo<any> = {
  code: 0,
  isLoading: false,
  isError: false,
  data: undefined
};

// 普通调用
export const swaggerFetch: SwaggerFetch = async (APIPath, ...params) => {
  try {
    const { status, message, data } = await APIPath(...params);
    if (status !== 0) throw new Error(JSON.stringify({ status, message }));
    return { isOk: true, data: data, code: 0 };
  } catch (err: any) {
    console.log(`[API ERROR]: API_NAME ${APIPath?.name}, ${err}`);
    return { isOk: false, data: null, code: JSON.parse(err.message).code };
  }
};

// 初始化调用
export function useSwaggerFetch<T extends APIPath<any>>(
  APIPath: T,
  params: Parameters<T>
): HookInfo<ReturnRequireData<T>> & { refresh: () => void; cancel?: (value: unknown) => void };
export function useSwaggerFetch<T extends APIPath<any>>(
  APIPath: T,
  params: Parameters<T>,
  deps: unknown[]
): HookInfo<ReturnRequireData<T>> & { refresh: () => void; cancel?: (value: unknown) => void };
export function useSwaggerFetch<T extends APIPath<any>>(
  APIPath: T,
  params: Parameters<T>,
  disabled: boolean
): HookInfo<ReturnRequireData<T>> & { refresh: () => void; cancel?: (value: unknown) => void };
export function useSwaggerFetch<T extends APIPath<any>>(
  APIPath: T,
  params: Parameters<T>,
  deps: unknown[],
  disabled: boolean
): HookInfo<ReturnRequireData<T>> & { refresh: () => void; cancel?: (value: unknown) => void };
export function useSwaggerFetch<T extends APIPath<any>>(
  APIPath: T,
  params: Parameters<T>,
  deps?: unknown[] | boolean,
  disabled?: boolean
): HookInfo<ReturnRequireData<T>> & { refresh: () => void; cancel?: (value: unknown) => void } {
  const unInvoke = typeof deps === 'boolean' ? deps : disabled;
  const dependencies = typeof deps === 'boolean' ? [] : deps || [];
  const [info, setInfo] = useState<HookInfo<ReturnRequireData<T>>>(INIT_INFO);
  const cancelRef = useRef<(value: unknown) => void>();
  // 入参合并 根据该接口的函数入参数量将cancel方法追加到最后一个参数中
  const getExtraParams = useCallback((parameterNum: number, params: Parameters<T>) => {
    const promise = new Promise(resolve => (cancelRef.current = resolve));
    const paramList = [...params];
    for (let i = 0; i < parameterNum; i++) {
      if (!paramList[i]) paramList.push({});
    }
    paramList[parameterNum - 1]['cancel'] = promise;
    return paramList;
  }, []);

  // 刷新
  const refresh = useCallback(async () => {
    cancelRef.current?.(''); // 取消上次请求
    setInfo(i => ({ ...i, isLoading: true, code: 0, isError: false }));
    try {
      const { status, message, data } = await APIPath(...getExtraParams(APIPath.length, params));
      if (status !== 0) throw new Error(JSON.stringify({ status, message }));
      setInfo(i => ({ ...i, data: data, code: 0, isLoading: false }));
    } catch (err: any) {
      setInfo(i => ({
        ...i,
        isError: true,
        isLoading: false,
        code: -1
      }));
      console.log(`[API ERROR]: API_NAME ${APIPath?.name}, ${err}`);
    }
  }, [...dependencies]);

  useEffect(() => {
    !unInvoke && refresh();
  }, [refresh, unInvoke]);

  return { ...info, refresh, cancel: cancelRef.current };
}

export default useSwaggerFetch;
